import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2, Calculator, ExternalLink, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import ReactMarkdown from 'react-markdown';
import DemoInterstitial from './DemoInterstitial';
import { PersistentCTABar, InlineConversionPrompt } from './DemoConversionPrompts';

// Demo bot ID - this is the fixed ID for the accounting demo bot
const DEMO_BOT_ID = 'c0000000-0000-4000-c000-000000000001';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Array<{ url: string; title?: string; snippet?: string }>;
}

interface AccountingDemoChatProps {
  onClose?: () => void;
}

const AccountingDemoChat: React.FC<AccountingDemoChatProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [showInterstitial, setShowInterstitial] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showInlinePrompt, setShowInlinePrompt] = useState(false);
  const [lastUserMessageIndex, setLastUserMessageIndex] = useState<number | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to user's question when assistant responds
  useEffect(() => {
    if (messages.length > 0 && 
        messages[messages.length - 1].role === 'assistant' && 
        lastUserMessageIndex !== null) {
      lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages, lastUserMessageIndex]);

  // Focus input when demo starts
  useEffect(() => {
    if (!showInterstitial && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInterstitial]);

  // Show inline conversion prompt after 3-5 messages
  useEffect(() => {
    if (messageCount >= 3 && messageCount <= 5 && !showInlinePrompt) {
      setShowInlinePrompt(true);
    }
  }, [messageCount, showInlinePrompt]);

  const handleStartDemo = () => {
    setShowInterstitial(false);
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: "Kia ora, I'm Rory — a governed AI accounting assistant. I can help explain New Zealand accounting and tax concepts using authoritative public sources. Ask anything you'd like."
    }]);
  };

  const handleGetAssistant = () => {
    navigate('/assistants/get-assistant');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setLastUserMessageIndex(messages.length); // Track index before adding
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setMessageCount(prev => prev + 1);

    try {
      const { data, error } = await supabase.functions.invoke('advisory-chat', {
        body: {
          bot_id: DEMO_BOT_ID,
          message: userMessage,
          conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
          session_id: `demo-${Date.now()}`,
        }
      });

      if (error) throw error;

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.answer || "I'm sorry, I couldn't generate a response.",
        citations: data.citations || []
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Demo chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologise, but I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (showInterstitial) {
    return (
      <div className="relative h-full">
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-2 right-2 z-10"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <DemoInterstitial 
          onStartDemo={handleStartDemo} 
          onGetAssistant={handleGetAssistant} 
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Header - fixed height */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-brand-teal/10 rounded-full">
            <Calculator className="h-5 w-5 text-brand-teal" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Accounting Advisory Assistant</h3>
            <p className="text-xs text-muted-foreground">Live Demo • NZ Accounting Knowledge</p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Messages - scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.map((message, index) => (
            <div key={index} ref={index === lastUserMessageIndex ? lastUserMessageRef : null}>
              <div
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-brand-teal text-white'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p>{message.content}</p>
                  )}
                  
                  {/* Citations */}
                  {message.citations && message.citations.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <p className="text-xs font-medium mb-2 opacity-70">Sources:</p>
                      <div className="space-y-1">
                        {message.citations.map((citation, cidx) => (
                          <a
                            key={cidx}
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 transition-opacity"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span className="truncate">{citation.title || citation.url}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show inline conversion prompt after user's 3rd-5th message */}
              {message.role === 'user' && showInlinePrompt && index === messages.length - 2 && messageCount >= 3 && messageCount <= 5 && (
                <InlineConversionPrompt onGetAssistant={handleGetAssistant} />
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-4 py-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Persistent CTA - fixed height */}
      <div className="flex-shrink-0">
        <PersistentCTABar onGetAssistant={handleGetAssistant} />
      </div>

      {/* Input - fixed height */}
      <form onSubmit={handleSubmit} className="flex-shrink-0 p-4 border-t border-border bg-card">
        <div className="flex gap-2 max-w-3xl mx-auto">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about NZ accounting, tax, or compliance..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isLoading || !input.trim()}
            className="bg-brand-teal hover:bg-brand-teal/90"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AccountingDemoChat;
